import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useLiveSource } from "@/hooks/useLiveSource";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { CATEGORY_LABEL, RAIL_LABEL, formatINR } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RotateCcw, Eye, Inbox, Zap, UserCheck, ExternalLink } from "lucide-react";
import { CopyableId } from "@/components/CopyableId";
import { encodeAdminPath } from "@/lib/adminRoutes";

const STATUS_STYLES = {
  pending_l1: "bg-warning/15 text-warning border-warning/30",
  pending_l2: "bg-warning/15 text-warning border-warning/30",
  pending_l3: "bg-warning/15 text-warning border-warning/30",
  processing_held: "bg-orange-100 text-orange-800 border-orange-300",
  awaiting_resubmission: "bg-secondary/15 text-secondary border-secondary/30",
  rejected: "bg-destructive/15 text-destructive border-destructive/30",
  success: "bg-success/15 text-success border-success/30",
  failed: "bg-destructive/15 text-destructive border-destructive/30",
};

/**
 * Look at a transaction's approval_chain and return any audit-trail badges
 * that should be displayed next to its row / in its timeline.
 *  - `super_admin_override`: at least one chain entry has `super_admin_shortcut: true`
 *  - `single_approver_walk`: 2+ entries by the same approver_id (i.e. an `approver_all` user)
 */
function getAuditFlags(chain) {
  const arr = Array.isArray(chain) ? chain : [];
  const superAdminOverride = arr.some((e) => e?.super_admin_shortcut === true);
  const ids = arr.map((e) => e?.approver_id).filter(Boolean);
  const counts = ids.reduce((m, id) => ((m[id] = (m[id] || 0) + 1), m), {});
  const singleApproverWalk = Object.values(counts).some((n) => n >= 2);
  return { superAdminOverride, singleApproverWalk };
}

function AuditBadges({ chain }) {
  const { superAdminOverride, singleApproverWalk } = getAuditFlags(chain);
  if (!superAdminOverride && !singleApproverWalk) return null;
  return (
    <div className="inline-flex gap-1 flex-wrap" data-testid="audit-badges">
      {superAdminOverride && (
        <Badge
          variant="outline"
          className="border-amber-400/50 bg-amber-50 text-amber-800 text-[10px] font-medium"
          data-testid="badge-super-admin-override"
          title="A super admin approved this in one click, auto-stamping all remaining levels."
        >
          <Zap className="h-3 w-3 mr-1" strokeWidth={2} />
          Super admin override
        </Badge>
      )}
      {singleApproverWalk && (
        <Badge
          variant="outline"
          className="border-purple-400/50 bg-purple-50 text-purple-800 text-[10px] font-medium"
          data-testid="badge-single-approver-walk"
          title="One approver (approver_all role) walked this txn through multiple levels."
        >
          <UserCheck className="h-3 w-3 mr-1" strokeWidth={2} />
          Single-approver walk
        </Badge>
      )}
    </div>
  );
}

export default function AdminApprovals() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "approver_all";
  const myLevel = /^approver_l[123]$/.test(user?.role || "")
    ? Number(user.role.split("_l")[1])
    : null;
  const [tab, setTab] = useState(myLevel ? `pending_l${myLevel}` : "pending_l1");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // {txn, action}
  const [remarks, setRemarks] = useState("");
  const [actionPassword, setActionPassword] = useState("");
  const [acting, setActing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [fullDetail, setFullDetail] = useState(null);
  const [fullDetailLoading, setFullDetailLoading] = useState(false);
  const { source } = useLiveSource();

  // Lazy-load the full audit payload (external API call logs) when the View
  // detail modal opens. Used for the inline bill-fetch req/response panel and
  // the "View full API call log" link.
  useEffect(() => {
    let cancelled = false;
    if (!selected || selected.action !== "view" || !selected.txn?.id) {
      setFullDetail(null);
      return;
    }
    setFullDetailLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/admin/all-txns/${selected.txn.id}/full`);
        if (!cancelled) setFullDetail(data);
      } catch {
        if (!cancelled) setFullDetail(null);
      } finally {
        if (!cancelled) setFullDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected?.action, selected?.txn?.id]);

  const refreshProviderStatus = async (txnId) => {
    setRefreshingStatus(true);
    try {
      const { data } = await api.post(`/admin/transactions/${txnId}/refresh-status`);
      if (data.ok && data.transaction) {
        setSelected((cur) => (cur && cur.txn?.id === txnId ? { ...cur, txn: data.transaction } : cur));
        setItems((cur) => cur.map((t) => (t.id === txnId ? data.transaction : t)));
        toast.success(`Provider says: ${data.result?.status || "—"}`);
      } else {
        toast.info(data.message || "Provider had nothing to report");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Status refresh failed");
    } finally {
      setRefreshingStatus(false);
    }
  };

  const load = async (status = tab) => {
    setLoading(true);
    try {
      let rows = [];
      if (source === "live") {
        // Live mode → relay /db-read on transactions. Map approval tabs
        // ("pending_l1" / "approved_l1" / ...) to the matching DB status.
        // For non-admin approvers, also enforce role-level filtering client-side
        // so live-mode doesn't accidentally surface levels the approver
        // wouldn't see in preview mode (the /admin/approvals endpoint applies
        // this gate server-side; db-read does not).
        const filters = {};
        if (status) filters.status = status;
        if (!isAdmin && myLevel) {
          // Approvers only see rows currently pending their level. Approved/
          // rejected history of other levels is hidden in preview mode too.
          if (status === `pending_l${myLevel}` || status === `approved_l${myLevel}`) {
            // OK — already targeted at this level.
          } else if (!status?.endsWith(`_l${myLevel}`)) {
            filters.status = `pending_l${myLevel}`;
          }
        }
        const { data } = await api.post("/admin/system-test/live-db", {
          table: "transactions",
          filters,
          sort_field: "created_at",
          sort_dir: -1,
          limit: 200,
        });
        rows = (data.rows || []).map((r) => ({
          ...r,
          // The PG endpoint already returns `display_id`; ensure minimal shape.
          id: r.id,
          status: r.status,
        }));

        // ─── Client-side enrichment for live mode ────────────────────
        // The /admin/approvals endpoint enriches server-side with partner info
        // + duplicate-bill flags. The relay's bare db-read does NOT — so we
        // mimic that enrichment here.
        const userIds = Array.from(new Set(rows.map((r) => r.user_id).filter(Boolean)));
        if (userIds.length) {
          try {
            const userRes = await api.post("/admin/system-test/live-db", {
              table: "users",
              filters: { id: { $in: userIds } },
              limit: userIds.length,
              projection: { id: 1, full_name: 1, email: 1, mobile: 1, business_name: 1 },
            });
            const byId = Object.fromEntries((userRes.data.rows || []).map((u) => [u.id, u]));
            rows = rows.map((r) => ({
              ...r,
              partner: {
                id: byId[r.user_id]?.id || r.user_id,
                full_name: byId[r.user_id]?.full_name || null,
                email: byId[r.user_id]?.email || null,
                mobile: byId[r.user_id]?.mobile || null,
                business_name: byId[r.user_id]?.business_name || null,
              },
            }));
          } catch { /* enrichment is best-effort */ }
        }

        // Duplicate-bill flag for BBPS rows in live mode. Same rule as server:
        // same customer_mobile + biller_id + consumer_id, status='success' only.
        const bbpsRows = rows.filter((r) =>
          r.type === "bbps" && r.customer_mobile && r.biller_id && r.consumer_id
        );
        await Promise.all(bbpsRows.map(async (r) => {
          try {
            const dup = await api.post("/admin/system-test/live-db", {
              table: "transactions",
              filters: {
                type: "bbps",
                status: "success",
                customer_mobile: r.customer_mobile,
                biller_id: r.biller_id,
                consumer_id: r.consumer_id,
              },
              sort_field: "created_at",
              sort_dir: -1,
              limit: 5,
              projection: { id: 1, display_id: 1, status: 1, amount: 1, created_at: 1, user_id: 1 },
            });
            const prior = (dup.data.rows || [])
              .filter((d) => d.id !== r.id)
              .map((d) => ({
                txn_id: d.id, display_id: d.display_id, status: d.status,
                amount: parseFloat(d.amount || 0), created_at: d.created_at, user_id: d.user_id,
              }));
            r.duplicate_for_customer = prior;
          } catch { /* best-effort */ }
        }));
      } else {
        const params = new URLSearchParams();
        if (isAdmin) params.set("status", status);
        const { data } = await api.get(`/admin/approvals?${params.toString()}`);
        rows = data.items || [];
      }
      setItems(rows);
      // Summary is in-process state, only meaningful for preview mode.
      if (source !== "live") {
        const s = await api.get("/approvals/me");
        setSummary(s.data);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab, source]);

  const onAction = async () => {
    if (!selected) return;
    if (selected.action !== "approve" && !remarks.trim()) {
      toast.error("Remarks are required for reject / request resubmit");
      return;
    }
    if (!actionPassword.trim()) {
      toast.error("Password is required to confirm this action");
      return;
    }
    setActing(true);
    try {
      const { data } = await api.post(`/admin/approvals/${selected.txn.id}/action`, {
        action: selected.action,
        remarks: remarks.trim() || undefined,
        password: actionPassword,
      });
      toast.success(
        selected.action === "approve"
          ? `Approved · status now ${data.status}`
          : selected.action === "reject"
          ? "Transaction rejected — wallet refunded"
          : "Resubmission requested — wallet refunded"
      );
      setSelected(null);
      setRemarks("");
      setActionPassword("");
      await load(tab);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
    } finally {
      setActing(false);
    }
  };

  const processNow = async (txnId) => {
    if (!confirm("Trigger the provider call for this held transaction?")) return;
    try {
      const { data } = await api.post(`/admin/approvals/${txnId}/process-now`, {});
      toast.success(`Processed · status now ${data.status}`);
      await load(tab);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Process failed");
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">{isAdmin ? "Admin" : `Approver L${myLevel}`}</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Approval queue
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            {isAdmin
              ? "All credit-card BBPS payments and payouts pass through L1 → L2 → L3."
              : `You can act on transactions waiting at level ${myLevel}.`}
          </p>
        </div>
        {summary && (
          <div className="flex items-center gap-3 text-xs">
            {[1, 2, 3].map((lvl) => (
              <div key={lvl} className="surface-card px-4 py-3 text-center min-w-[80px]" data-testid={`queue-l${lvl}-count`}>
                <div className="overline">L{lvl}</div>
                <div className="font-mono text-xl mt-1">{summary.counts[`pending_l${lvl}`] || 0}</div>
              </div>
            ))}
            <div className="surface-card px-4 py-3 text-center min-w-[80px]" data-testid="queue-resubmit-count">
              <div className="overline">Resub</div>
              <div className="font-mono text-xl mt-1">{summary.counts.awaiting_resubmission || 0}</div>
            </div>
            <div className="surface-card px-4 py-3 text-center min-w-[80px] border-l-4 border-l-warning" data-testid="queue-held-count">
              <div className="overline text-warning">Held</div>
              <div className="font-mono text-xl mt-1">{summary.counts.processing_held || 0}</div>
            </div>
          </div>
        )}
      </div>

      {isAdmin && (
        <Tabs value={tab} onValueChange={setTab} className="mt-6" data-testid="approval-tabs">
          <TabsList>
            <TabsTrigger value="pending_l1" data-testid="tab-l1">L1</TabsTrigger>
            <TabsTrigger value="pending_l2" data-testid="tab-l2">L2</TabsTrigger>
            <TabsTrigger value="pending_l3" data-testid="tab-l3">L3</TabsTrigger>
            <TabsTrigger value="awaiting_resubmission" data-testid="tab-resubmit">Awaiting resubmit</TabsTrigger>
            <TabsTrigger value="processing_held" data-testid="tab-held">Held</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      <div className="mt-6 surface-card overflow-hidden">
        <Table data-testid="approvals-table">
          <TableHeader>
            <TableRow>
              <TableHead>Submitted</TableHead>
              <TableHead>Txn ID</TableHead>
              <TableHead>Partner</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Detail</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Duplicate</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                  <Inbox className="h-6 w-6 mx-auto mb-2" strokeWidth={1.5} />
                  Queue is empty.
                </TableCell>
              </TableRow>
            ) : items.map((t) => {
              const txnLevel = t.status?.startsWith("pending_l") ? Number(t.status.split("_l")[1]) : null;
              const canAct = isAdmin || (txnLevel && txnLevel === myLevel);
              return (
                <TableRow key={t.id} data-testid={`approval-row-${t.id}`} className={t.duplicate_for_customer?.length ? "bg-amber-50/50 dark:bg-amber-950/10" : ""}>
                  <TableCell className="text-xs font-mono text-muted-foreground whitespace-nowrap">
                    {new Date(t.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </TableCell>
                  <TableCell className="text-xs">
                    <CopyableId value={t.display_id || t.id} />
                    {t.duplicate_for_customer?.length > 0 && (
                      <div
                        data-testid={`dup-flag-${t.id}`}
                        title={`This customer has already PAID this same bill ${t.duplicate_for_customer.length}× before.`}
                        className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 border border-amber-300"
                      >
                        ⚠ Duplicate · {t.duplicate_for_customer.length}×
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-sm">{t.partner?.full_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{t.partner?.business_name || t.partner?.email}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="uppercase font-mono text-[10px]">{t.type}</Badge>
                    <div className="text-xs text-muted-foreground mt-1">
                      {CATEGORY_LABEL[t.category] || RAIL_LABEL[t.category] || t.category}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {t.type === "bbps" ? (
                      <div>
                        <div>{t.consumer_name || "—"}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">{t.consumer_id}</div>
                      </div>
                    ) : (
                      <div>
                        <div>{t.beneficiary_snapshot?.nickname}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">
                          {t.beneficiary_snapshot?.upi_id ||
                            `••••${(t.beneficiary_snapshot?.account_number || "").slice(-4)} · ${t.beneficiary_snapshot?.ifsc}`}
                        </div>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{formatINR(t.amount)}</TableCell>
                  <TableCell>
                    {(() => {
                      // Derive duplicate state from any available signal — keeps
                      // the column accurate even when the backend hasn't been
                      // re-deployed with the new `is_duplicate` flag.
                      const dupCustCount = t.duplicate_for_customer?.length || 0;
                      const dupPayoutCount = t.duplicate_for_payout?.length || 0;
                      const totalDup = dupCustCount + dupPayoutCount;
                      const isDup = t.is_duplicate || totalDup > 0;
                      return isDup ? (
                        <Badge
                          variant="outline"
                          className="bg-amber-100 text-amber-900 border-amber-300 font-bold text-[10px]"
                          title={
                            dupCustCount
                              ? `Customer already paid this bill ${dupCustCount}× before`
                              : dupPayoutCount
                                ? `Same payout sent ${dupPayoutCount}× in last 24h`
                                : "Potential duplicate detected"
                          }
                          data-testid={`dup-col-${t.id}`}
                        >
                          ⚠ Yes{totalDup ? ` · ${totalDup}×` : ""}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground text-[10px]" data-testid={`dup-col-${t.id}`}>
                          No
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_STYLES[t.status] || ""}>{t.status}</Badge>
                    {t.resubmit_count > 0 && <div className="text-[10px] text-muted-foreground mt-1">resubmit #{t.resubmit_count}</div>}
                    <div className="mt-1"><AuditBadges chain={t.approval_chain} /></div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setSelected({ txn: t, action: "view" })} data-testid={`view-${t.id}`} aria-label="View">
                        <Eye className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => window.open(`/admin/approvals/${t.id}`, "_blank")}
                        data-testid={`open-detail-${t.id}`}
                        title="Open full detail page in new tab"
                      >
                        <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                      {canAct && t.status?.startsWith("pending_l") && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setSelected({ txn: t, action: "approve" })} data-testid={`approve-${t.id}`}>
                            <CheckCircle2 className="h-4 w-4 mr-1 text-success" strokeWidth={1.5} />Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setSelected({ txn: t, action: "request_resubmit" })} data-testid={`resubmit-${t.id}`}>
                            <RotateCcw className="h-4 w-4 mr-1 text-secondary" strokeWidth={1.5} />Request resubmit
                          </Button>
                          <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setSelected({ txn: t, action: "reject" })} data-testid={`reject-${t.id}`}>
                            <XCircle className="h-4 w-4 mr-1" strokeWidth={1.5} />Reject
                          </Button>
                        </>
                      )}
                      {isAdmin && t.status === "processing_held" && (
                        <Button size="sm" variant="outline" className="border-orange-300 text-orange-800 hover:bg-orange-50" onClick={() => processNow(t.id)} data-testid={`process-now-${t.id}`}>
                          <Zap className="h-4 w-4 mr-1" strokeWidth={1.5} />Process now
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={selected !== null} onOpenChange={(o) => !o && (setSelected(null), setRemarks(""))}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selected?.action === "view" && "Transaction details"}
              {selected?.action === "approve" && `Approve at L${selected.txn?.status?.split("_l")[1]}`}
              {selected?.action === "reject" && "Reject transaction"}
              {selected?.action === "request_resubmit" && "Request resubmission"}
            </DialogTitle>
            <DialogDescription>
              {selected?.txn?.display_id && (
                <span className="inline-flex items-center gap-1 mr-2">
                  Txn ID: <CopyableId value={selected.txn.display_id} />
                </span>
              )}
              {selected?.action === "approve" && "Move to the next approval level."}
              {selected?.action === "reject" && "Final rejection. Wallet will be refunded automatically."}
              {selected?.action === "request_resubmit" && "Wallet refunded; partner will be asked to re-enter details."}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <F label="Amount" v={formatINR(selected.txn.amount)} mono />
                <F label="Platform fee" v={formatINR(selected.txn.platform_fee)} mono />
                <F label="Wallet debit" v={formatINR(selected.txn.wallet_debit)} mono />
                <F label="Status" v={selected.txn.status} mono />
                {selected.txn.type === "bbps" ? (
                  <>
                    <F label="Consumer" v={selected.txn.consumer_name} />
                    <F label="Consumer ID" v={selected.txn.consumer_id} mono />
                  </>
                ) : (
                  <>
                    <F label="Beneficiary" v={selected.txn.beneficiary_snapshot?.nickname} />
                    <F label="Account" v={selected.txn.beneficiary_snapshot?.upi_id || `••••${(selected.txn.beneficiary_snapshot?.account_number || "").slice(-4)} · ${selected.txn.beneficiary_snapshot?.ifsc}`} mono />
                  </>
                )}
              </div>

              {/* Partner card */}
              {selected.txn.partner && (
                <div className="rounded-md border border-border bg-muted/30 p-3" data-testid="modal-partner-block">
                  <div className="overline mb-2">Partner</div>
                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <F label="Name" v={selected.txn.partner.full_name} />
                    <F label="Business" v={selected.txn.partner.business_name} />
                    <F label="Mobile" v={selected.txn.partner.mobile} mono />
                    <F label="Email" v={selected.txn.partner.email} mono />
                  </div>
                </div>
              )}

              {/* Customer + CA input details (BBPS only) */}
              {selected.txn.type === "bbps" && (
                <div className="rounded-md border border-border bg-muted/30 p-3" data-testid="modal-ca-block">
                  <div className="overline mb-2">Customer &amp; consumer-account inputs</div>
                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <F label="Customer mobile" v={selected.txn.customer_mobile} mono />
                    <F label="Biller" v={selected.txn.biller_name || selected.txn.biller_id} />
                    <F label="Category" v={selected.txn.category} mono />
                    <F label="Consumer name" v={selected.txn.consumer_name} />
                    <F label="Consumer ID" v={selected.txn.consumer_id} mono />
                    {selected.txn.consumer_card_id && (
                      <F label="Saved card ref" v={selected.txn.consumer_card_id.slice(0, 8) + "…"} mono />
                    )}
                  </div>
                  {(() => {
                    let ad = selected.txn.ad_fields
                      || selected.txn.consumer_card_snapshot?.ad_fields
                      || {};
                    // Tolerate legacy rows where ad_fields was persisted as a
                    // JSON-encoded string instead of a dict.
                    if (typeof ad === "string") {
                      try { ad = JSON.parse(ad); } catch { ad = {}; }
                    }
                    if (!ad || typeof ad !== "object") ad = {};
                    const entries = Object.entries(ad).filter(([, v]) => v !== null && v !== "" && v !== undefined);
                    if (entries.length === 0) return null;
                    return (
                      <div className="mt-3">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Additional CA inputs (ad_fields)</div>
                        <div className="grid sm:grid-cols-2 gap-x-3 gap-y-1 text-xs">
                          {entries.map(([k, v]) => (
                            <div key={k} className="flex justify-between gap-2 border-l-2 border-border pl-2">
                              <span className="text-muted-foreground font-mono">{k}</span>
                              <span className="font-mono break-all text-right">{String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Bill fetch req/response (BBPS only) — lazy loaded from /admin/all-txns/{id}/full */}
              {selected.txn.type === "bbps" && (
                <BillFetchPanel
                  loading={fullDetailLoading}
                  fullDetail={fullDetail}
                  txnId={selected.txn.id}
                />
              )}

              {selected.txn.duplicate_for_customer?.length > 0 && (
                <div data-testid="dup-detail-block" className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700 p-3">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-amber-900 dark:text-amber-200 text-sm">⚠ Same customer already paid this bill</span>
                    <Badge variant="outline" className="text-[10px] bg-amber-100 border-amber-300 text-amber-900">
                      {selected.txn.duplicate_for_customer.length} prior payment{selected.txn.duplicate_for_customer.length > 1 ? "s" : ""}
                    </Badge>
                  </div>
                  <p className="text-xs text-amber-900/80 dark:text-amber-200/70 mt-1">
                    Customer mobile <span className="font-mono font-semibold">{selected.txn.customer_mobile}</span> previously paid
                    biller <span className="font-mono font-semibold">{selected.txn.biller_name || selected.txn.biller_id}</span> ·
                    consumer <span className="font-mono font-semibold">{selected.txn.consumer_id}</span>:
                  </p>
                  <ul className="mt-2 space-y-1 text-xs">
                    {selected.txn.duplicate_for_customer.map((d, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 border-l-2 border-amber-400 pl-2 py-0.5">
                        <span className="font-mono">{d.display_id || d.txn_id}</span>
                        <span className="text-muted-foreground">{new Date(d.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</span>
                        <span className="font-mono">{formatINR(d.amount)}</span>
                        <Badge variant="outline" className="text-[9px] uppercase">{d.status}</Badge>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selected.txn.approval_chain?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between">
                    <span className="overline">Approval timeline</span>
                    <AuditBadges chain={selected.txn.approval_chain} />
                  </div>
                  <ol className="mt-2 space-y-1 text-xs">
                    {selected.txn.approval_chain.map((e, i) => (
                      <li key={i} className="flex items-baseline justify-between gap-3 border-l-2 border-border pl-3 py-1">
                        <span>
                          <span className="font-semibold">L{e.level}</span> · {e.action} ·{" "}
                          <span className="text-muted-foreground">{e.approver_email}</span>
                          {e.super_admin_shortcut && (
                            <Badge variant="outline" className="ml-2 border-amber-400/50 bg-amber-50 text-amber-800 text-[9px] py-0">
                              <Zap className="h-2.5 w-2.5 mr-0.5" strokeWidth={2} />auto
                            </Badge>
                          )}
                          {e.remarks && <span className="text-muted-foreground"> — {e.remarks}</span>}
                        </span>
                        <span className="font-mono text-muted-foreground">{new Date(e.decided_at).toLocaleTimeString()}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {(selected.txn.message || selected.txn.provider_response) && (
                <div className="border border-destructive/30 bg-destructive/5 rounded-md p-3">
                  <span className="overline text-destructive">Provider response</span>
                  {selected.txn.message && (
                    <div className="mt-1 text-sm font-mono break-words" data-testid="provider-message">
                      {selected.txn.message}
                    </div>
                  )}
                  {selected.txn.provider_response && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer text-muted-foreground">Raw response</summary>
                      <pre className="mt-1 bg-background rounded p-2 overflow-x-auto text-[10px]">
                        {JSON.stringify(selected.txn.provider_response, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}
              {(selected.txn.payout_last_status_response || selected.txn.payout_status_checked_at) && (
                <div className="border border-border rounded-md p-3 bg-muted/30" data-testid="admin-status-check-section">
                  <div className="flex items-center justify-between">
                    <span className="overline">Provider status check</span>
                    <button
                      type="button"
                      data-testid={`refresh-status-${selected.txn.id}`}
                      disabled={refreshingStatus}
                      onClick={() => refreshProviderStatus(selected.txn.id)}
                      className="text-xs font-semibold underline-offset-2 hover:underline disabled:opacity-60"
                    >
                      {refreshingStatus ? "Refreshing…" : "Refresh now"}
                    </button>
                  </div>
                  <div className="mt-2 grid sm:grid-cols-2 gap-1.5 text-xs">
                    <div><span className="text-muted-foreground">Last checked:</span> <span className="font-mono">{selected.txn.payout_status_checked_at ? new Date(selected.txn.payout_status_checked_at).toLocaleString() : "—"}</span></div>
                    <div><span className="text-muted-foreground">Provider says:</span> <span className="font-mono font-semibold">{selected.txn.payout_last_status_response?.status || "—"}</span></div>
                    {selected.txn.payout_last_status_response?.operator_ref && (
                      <div><span className="text-muted-foreground">Operator ref / UTR:</span> <span className="font-mono">{selected.txn.payout_last_status_response.operator_ref}</span></div>
                    )}
                    {selected.txn.payout_last_status_response?.amount && (
                      <div><span className="text-muted-foreground">Settled amount:</span> <span className="font-mono">{selected.txn.payout_last_status_response.amount}</span></div>
                    )}
                  </div>
                  {selected.txn.payout_last_status_response?.message && (
                    <div className="mt-2 text-xs italic text-muted-foreground">{selected.txn.payout_last_status_response.message}</div>
                  )}
                  {selected.txn.payout_last_status_response?.raw && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer text-muted-foreground">Raw status response</summary>
                      <pre className="mt-1 bg-background rounded p-2 overflow-x-auto text-[10px]">
                        {JSON.stringify(selected.txn.payout_last_status_response.raw, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}
              {selected.action === "view" && !(selected.txn.payout_last_status_response || selected.txn.payout_status_checked_at) && ["success", "failed", "processing", "pending"].includes((selected.txn.status || "").toLowerCase()) && (
                <div className="border border-dashed border-border rounded-md p-3 text-xs flex items-center justify-between" data-testid="status-check-empty">
                  <span className="text-muted-foreground">No provider status pulled yet.</span>
                  <button
                    type="button"
                    data-testid={`refresh-status-empty-${selected.txn.id}`}
                    disabled={refreshingStatus}
                    onClick={() => refreshProviderStatus(selected.txn.id)}
                    className="font-semibold underline-offset-2 hover:underline disabled:opacity-60"
                  >
                    {refreshingStatus ? "Refreshing…" : "Pull status from provider"}
                  </button>
                </div>
              )}
              {selected.action === "view" && (
                <div className="flex justify-end pt-2 border-t border-border" data-testid="modal-full-audit-link">
                  <a
                    href={`${encodeAdminPath("/admin/all-txns")}?txn=${encodeURIComponent(selected.txn.id)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                  >
                    View full API call log
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              {selected.action !== "view" && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="rmk">Remarks {selected.action !== "approve" && <span className="text-destructive">*</span>}</Label>
                    <Textarea id="rmk" data-testid="action-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1" rows={3} placeholder={selected.action === "request_resubmit" ? "Explain what details the partner should change…" : "Reason"} />
                  </div>
                  <div>
                    <Label htmlFor="action-pwd">Your password <span className="text-destructive">*</span></Label>
                    <Input
                      id="action-pwd"
                      data-testid="action-password"
                      type="password"
                      value={actionPassword}
                      onChange={(e) => setActionPassword(e.target.value)}
                      className="mt-1"
                      placeholder="Enter your login password to confirm"
                      autoComplete="off"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Required for all approval actions</p>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {selected?.action === "view" ? (
              <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
                <Button
                  onClick={onAction}
                  disabled={acting}
                  data-testid={`confirm-${selected?.action}`}
                  className={
                    selected?.action === "reject" ? "bg-destructive hover:bg-destructive/90" :
                    selected?.action === "request_resubmit" ? "bg-secondary hover:bg-secondary/90" :
                    ""
                  }
                >
                  {acting ? "Working…" : `Confirm ${selected?.action.replace("_", " ")}`}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function F({ label, v, mono }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className={`mt-1 ${mono ? "font-mono" : ""}`}>{v || "—"}</div>
    </div>
  );
}

/**
 * Compact bill-fetch req/response panel — extracts the most recent bill-fetch
 * call from the txn's external_api_logs and renders a collapsible JSON view
 * for both the outgoing request and the response body.
 */
function BillFetchPanel({ loading, fullDetail, txnId }) {
  if (loading) {
    return (
      <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground" data-testid="bill-fetch-loading">
        Loading bill-fetch log…
      </div>
    );
  }
  const logs = fullDetail?.api_logs || [];
  // Identify bill-fetch entries. Match by endpoint URL keywords first; fall
  // back to outbound provider key when present.
  const billFetchLog = logs.find((l) => {
    const url = `${l.url || ""} ${l.endpoint || ""} ${l.request_path || ""}`.toLowerCase();
    return url.includes("bill") && (url.includes("fetch") || url.includes("inquiry") || url.includes("billerinfo"));
  }) || logs.find((l) => (l.action || "").toLowerCase().includes("fetch"));

  if (!billFetchLog) {
    return (
      <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground" data-testid="bill-fetch-empty">
        No bill-fetch API log found for this transaction.
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border bg-muted/30 p-3" data-testid="bill-fetch-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="overline">Bill fetch · provider call</div>
        <span className="text-[10px] font-mono text-muted-foreground">
          {billFetchLog.status_code ? `HTTP ${billFetchLog.status_code}` : "—"}
          {billFetchLog.created_at ? ` · ${new Date(billFetchLog.created_at).toLocaleString()}` : ""}
        </span>
      </div>
      <div className="text-xs font-mono break-all text-muted-foreground mb-2">
        {(billFetchLog.method || "POST")} {billFetchLog.url || billFetchLog.endpoint || "—"}
      </div>
      <details className="text-xs">
        <summary className="cursor-pointer font-semibold">Request</summary>
        <pre className="mt-1 bg-background rounded p-2 overflow-x-auto text-[10px] max-h-60">
          {tryStringify(billFetchLog.request_body)}
        </pre>
      </details>
      <details className="text-xs mt-2">
        <summary className="cursor-pointer font-semibold">Response</summary>
        <pre className="mt-1 bg-background rounded p-2 overflow-x-auto text-[10px] max-h-60">
          {tryStringify(billFetchLog.response_body)}
        </pre>
      </details>
    </div>
  );
}

function tryStringify(v) {
  if (v == null) return "—";
  if (typeof v === "string") {
    try { return JSON.stringify(JSON.parse(v), null, 2); } catch { return v; }
  }
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}
